import { MessagePathMetadataWellKnown } from "@/shared/domain/topics";
import { scaleForUnit } from "@/shared/domain/urdf";
import type {
  Joint,
  JointValue,
} from "@/shared/state/visualization/schema/v1/three-d";
import type { TopicData } from "@/shared/state/visualization/schema/v1/topic-data";

import { jointUnit } from "./jointControl";
import { joinedPathFor } from "./PositionConfig/positionTopicData";

/**
 * The constant a joint's path field is holding, if what was typed is one.
 *
 * A joint's value is `TopicData | number` in the schema, and the renderer
 * already resolves the number without asking the data server. The only thing
 * missing was a way to say it, and the answer is the field that is already
 * there: a path names data, and anything that reads as a finite number names a
 * fixed pose.
 *
 * Not finite is not a pose. `NaN` and `±Infinity` parse as numbers and would
 * reach Babylon as a transform that makes the model disappear, so they are
 * refused here alongside text that is not numeric at all — the joint stays
 * visibly unbound, which someone can see, rather than silently becoming 0.
 *
 * The caller resolves the text as a *path* first: `0.5` is syntactically also
 * "topic `0`, field `5`", and a real topic named `0` has to keep winning.
 */
export function jointConstantFor(text: string): number | undefined {
  const trimmed = text.trim();
  if (trimmed === "") {
    return undefined;
  }
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : undefined;
}

/**
 * What a joint's value reads as in its path field — a path, or the constant
 * itself.
 *
 * The inverse of {@link jointConstantFor}, so a value saved in a workspace
 * re-opens as the text it was authored in. `0` is written out as `"0"` rather
 * than as an empty field: an unbound joint is *pinned at zero*, which is a
 * decision the field has to show, and not the same as a joint nobody has
 * decided about.
 */
export function jointValueText(
  value: TopicData | number | undefined,
): string | undefined {
  if (typeof value === "number") {
    return String(value);
  }
  return joinedPathFor(value);
}

/**
 * The unit the field catalogue says a bound path is published in, if it says.
 *
 * Read off the message path the workspace already holds — the same record the
 * plot panels read their axis units from — so no extra request is made and the
 * answer cannot drift from the tree the path was picked out of.
 */
function unitOf(topicData: TopicData): string | undefined {
  const unit =
    topicData.messagePath.metadata[MessagePathMetadataWellKnown.Unit];
  return typeof unit === "string" ? unit : undefined;
}

/**
 * The scale that carries a joint's data into the joint's own units.
 *
 * A URDF states its limits in radians (or metres), and the renderer applies a
 * joint value in exactly those. A topic publishing **degrees** into a joint read
 * as radians is a robot that spins: 90 read as radians is about fourteen turns
 * past a ±1.57 limit, and nothing downstream stops it — `drivenValues` clamps
 * *authored* values only, deliberately, because recorded data is an observation
 * and folding it back inside the declared range would hide both the mistake and
 * a real robot's genuine excursions past its own limits. The conversion is
 * therefore the whole defence, and it is `scaleForUnit` — the same function the
 * ingest wizard's rows use, so the two surfaces cannot disagree about one topic.
 *
 * Two cases come out as 1, and they are different things:
 *
 * - **A constant.** The user typed the joint's value in the joint's own units.
 *   Converting it would be converting a number that is already right.
 * - **No unit reported**, which is the common case — the catalogue returns
 *   `unit: null` for most joint fields. `scaleForUnit` also returns `undefined`
 *   for a unit that already *is* SI, so both fall through to the extension's
 *   own assumption of radians or metres. That assumption is *stated on the row*
 *   by {@link jointValueNote} rather than made silently.
 */
export function jointScaleFor(value: TopicData | number): number {
  if (typeof value === "number") {
    return 1;
  }
  return scaleForUnit(unitOf(value)) ?? 1;
}

/**
 * What a joint's row says about the value it is holding, in a sentence.
 *
 * The unit half of a binding is invisible in the path itself, and getting it
 * wrong is not a subtle failure — so the row states which of the two happened:
 * a conversion the catalogue justified, or the SI assumption made in the absence
 * of one.
 *
 * The note states what the value *is*, not where the joint ends up: a constant
 * outside the joint's travel is held inside it by `drivenValues`, and the row
 * already shows the limits it is held inside beside the joint's name.
 */
export function jointValueNote(
  joint: Joint,
  value: JointValue | undefined,
): string | undefined {
  if (value === undefined) {
    return undefined;
  }
  const unit = jointUnit(joint);
  if (typeof value.value === "number") {
    return `Held at ${value.value} ${unit}`;
  }
  if (value.scale === 1) {
    return `Assumed to be in ${unit}`;
  }
  return `Scaled by ${Number.parseFloat(value.scale.toPrecision(3))} into ${unit}`;
}
